<template>
  <div>
    <InfoCard body-class="p-2">
      <p class="text-size-14">
        お仕事をする上で経験や転職意欲の入力は必須です<br />
        入力することで以下のことがスムーズに行えます<br />
        ✓お仕事への応募<br />
        ✓事業所様とのお仕事の調整<br />
        ✓転職活動
      </p>
    </InfoCard>
    <div class="mt-2">
      <label class="mr-1 mb-1 text-size-14 text-mild">サービス種類・経験</label>
      <AppLabel variant="red">必須</AppLabel>
      <p class="mb-2 text-size-14 text-gray-05">
        経歴を全て選択
      </p>
      <BRow
        class="mx-n2"
        v-for="(experience, i) in inputValues.experiences"
        :key="i"
      >
        <BCol cols="6" class="px-2">
          <ValidationProvider
            v-slot="validationState"
            name="種類"
            rules="required"
            :vid="`experiences[${i}].service`"
            v-model="experience.service"
          >
            <AppFormSelect
              v-model="experience.service"
              :formOptions="
                isIncludeProductService(experience.service)
                  ? options.experienceTypeOptionsByProduct
                  : options.experienceTypeOptions
              "
              firstOption="種類を選択"
              @change="onChangeService(i)"
              :validation-state="validationState"
              :excludeSelected="true"
              :selectedOptions="selectedExperiences"
              :disabled="!isIncludeProductService(experience.service)"
            />
            <FormInvalidFeedback
              :validation-state="validationState"
              :state="false"
            />
          </ValidationProvider>
        </BCol>
        <BCol cols="4" class="px-2">
          <ValidationProvider
            v-if="!isInexperienced(experience.service)"
            v-slot="validationState"
            name="経験"
            rules="required"
            :vid="`experiences[${i}].stint`"
            v-model="experience.stint"
          >
            <AppFormSelect
              v-model="experience.stint"
              :formOptions="options.experienceStintOptions"
              firstOption="年数を選択"
              :validation-state="validationState"
              :disabled="!isIncludeProductService(experience.service)"
            />
            <FormInvalidFeedback
              :validation-state="validationState"
              :state="false"
            />
          </ValidationProvider>
        </BCol>
        <BCol cols="2" class="px-2" align-self="center">
          <FeatherIcon
            v-if="i === 0"
            icon="plus-circle"
            class="align-baseline"
            :class="{ 'text-gray-04': !isSelectedBeforeExperience }"
            @click="addExperience"
          />
          <FeatherIcon
            v-else
            icon="minus-circle"
            class="align-baseline"
            @click="removeExperience(i)"
          />
        </BCol>
      </BRow>
    </div>
    <ValidationProvider
      v-slot="validationState"
      name="自己PR"
      rules="required|max:512"
      tag="div"
      class="mt-3"
      vid="prComment"
      v-model="inputValues.prComment"
    >
      <TextAreaGroup
        v-model="inputValues.prComment"
        appLabelText="必須"
        label="自己PR"
        placeholder="経歴/現在の働き方、得意な業務、利用頻度、興味、挨拶言葉など"
        type="text"
        :validation-state="validationState"
      />
    </ValidationProvider>
    <ValidationField
      v-slot="validationState"
      label="現在の働き方"
      rules="required"
      tag="div"
      class="mt-3"
      name="latestWorkStyle"
      v-model="inputValues.latestWorkStyle"
    >
      <FormCheckboxGroup
        label="現在の働き方（複数可）"
        appLabelText="必須"
        name="latest"
        :options="options.workStyleOptions"
        v-model="inputValues.latestWorkStyle"
        :validation-state="validationState"
        @change="onChangeWorkStyles($event, validationState.handleChange)"
      />
    </ValidationField>
    <ValidationProvider
      v-slot="validationState"
      name="転職意欲"
      rules="required"
      tag="div"
      class="mt-3"
      vid="jobChange"
      v-model="inputValues.jobChange"
    >
      <FormSelectGroup
        label="転職意欲"
        appLabelText="必須"
        v-model="inputValues.jobChange"
        :options="options.jobChangeOptions"
        :validation-state="validationState"
      />
    </ValidationProvider>
    <ValidationField
      v-slot="validationState"
      label="次の希望の働き方"
      rules="required"
      tag="div"
      class="mt-3"
      name="preferredWorkStyle"
      v-model="inputValues.preferredWorkStyle"
    >
      <FormCheckboxGroup
        label="次の希望の働き方（複数可）"
        appLabelText="必須"
        name="preferred"
        :options="options.workStyleOptions"
        v-model="inputValues.preferredWorkStyle"
        :validation-state="validationState"
        @change="onChangeWorkStyles($event, validationState.handleChange)"
      />
    </ValidationField>
  </div>
</template>

<script lang="ts">
import { accountService } from "@/app/services/AccountService";
import { workStyleService } from "@/app/services/WorkStyleService";
import AppFormSelect from "@/components/atoms/input/AppFormSelect.vue";
import AppLabel from "@/components/atoms/input/AppLabel.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import FormInvalidFeedback from "@/components/atoms/other/FormInvalidFeedback.vue";
import InfoCard from "@/components/atoms/other/InfoCard.vue";
import FormCheckboxGroup from "@/components/molecules/form/FormCheckboxGroup.vue";
import FormSelectGroup from "@/components/molecules/form/FormSelectGroup.vue";
import TextAreaGroup from "@/components/molecules/form/TextAreaGroup.vue";
import { experienceStint } from "@/models/experienceStint";
import { authStore } from "@/store/auth";
import { masterStore } from "@/store/master";
import { BusinessInfoInputValues } from "@/types/form/BusinessInfoInputValues";
import { BusinessInfoOptions } from "@/types/form/BusinessInfoOptions";
import { HandleChangeType } from "@/types/Validation";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
import { Component, Prop, Vue } from "vue-facing-decorator";

@Component({
  components: {
    AppFormSelect,
    AppLabel,
    FeatherIcon,
    FormInvalidFeedback,
    FormSelectGroup,
    FormCheckboxGroup,
    InfoCard,
    TextAreaGroup
  }
})
export default class BusinessInfo extends Vue {
  @Prop({
    type: Object,
    required: true
  })
  value!: BusinessInfoInputValues;

  options: BusinessInfoOptions = {
    experienceTypeOptions: [],
    experienceTypeOptionsByProduct: [],
    experienceStintOptions: experienceStint,
    workStyleOptions: [],
    jobChangeOptions: []
  };

  get isCaitechPharmacistProduct() {
    return authStore.isCaitechPharmacistProduct;
  }

  get inputValues() {
    return this.value;
  }

  set inputValues(newVal) {
    if (this.value !== newVal) this.$emit("input", newVal);
  }

  get isSelectedBeforeExperience() {
    return (
      this.inputValues.experiences.every(e => e.service) &&
      this.inputValues.experiences.every(e => e.stint)
    );
  }

  get selectedExperiences() {
    return this.inputValues.experiences.map(experience => ({
      value: experience.service
    }));
  }

  async created() {
    try {
      this.$loading.show();
      await Promise.all([
        this.fetchWorkStyleOptions(),
        this.fetchJobChangeOptions()
      ]);
      this.setExperienceTypeOptions();
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  async fetchWorkStyleOptions() {
    const { results } = await workStyleService.fetchWorkStyle();
    this.options.workStyleOptions = results
      .filter(v =>
        this.isCaitechPharmacistProduct ? v.style !== "夜勤専従" : true
      )
      .map(v => ({
        text: v.style,
        value: v.id
      }));
  }

  async fetchJobChangeOptions() {
    const fetchedJobChangeList = await accountService.fetchJobChangeList();
    this.options.jobChangeOptions = fetchedJobChangeList.map(v => ({
      text: v.level,
      value: v.id
    }));
  }

  setExperienceTypeOptions() {
    // サービス種別（全て）
    // 現在のプロダクト種別では取り扱えないものが設定されている場合（介護アプリで「特養」を設定して、薬剤師アプリでこの画面に来る）、
    // select の選択肢のリストにそれが含まれていないと空に見えてしまうので、すべてのサービス種別を取得し、上記のケースの場合に formOptions に設定する
    this.options.experienceTypeOptions = masterStore.services.map(v => ({
      text: v.name,
      value: v.id
    }));

    // サービス種別（プロダクト種別でフィルタリングされたもの）
    this.options.experienceTypeOptionsByProduct = masterStore.servicesForWorkerExperience.map(
      v => ({
        text: v.name,
        value: v.id
      })
    );
  }

  addExperience() {
    if (!this.isSelectedBeforeExperience) return;
    this.inputValues.experiences.push({ service: null, stint: null });
  }

  removeExperience(i: number) {
    this.inputValues.experiences.splice(i, 1);
  }

  isInexperienced(service: number | null) {
    if (service === null) return false;
    return this.options.experienceTypeOptions.some(
      v => v.value === service && v.text === "未経験"
    );
  }

  isIncludeProductService(serviceId: number | null) {
    // 新規追加の場合は、serviceId が null になるので有効にしておく
    if (serviceId === null) return true;

    return masterStore.servicesForWorkerExperience.some(
      service => service.id === serviceId
    );
  }

  onChangeService(index: number) {
    if (this.isInexperienced(this.inputValues.experiences[index].service)) {
      this.inputValues.experiences[index].stint = null;
    }
  }

  onChangeWorkStyles(values: number[], handleChange: HandleChangeType) {
    // バリデーションエラーの表示を切り替えるため、handleChange イベントを発火する
    handleChange(values, values.length > 0);
  }
}
</script>

<style scoped>
.fixed-radio-group >>> .radio-group {
  display: grid;
  grid-template-columns: auto auto;
  row-gap: 12px;
}

.fixed-radio-group >>> .radio-group__item {
  margin-top: 0;
}
</style>
